import type { PublicPlayerStatus } from '@/api/public'

export type PlayerPublicStatusKind = 'playing' | 'spectating' | 'site-online' | 'offline'

export type PlayerPublicStatusPresentation = {
  kind: PlayerPublicStatusKind
  label: string
  liveID: string | null
  title: string
}

export type PlayerPublicStatusRequestFence = {
  requestID: number
  steamID: string
}

export type PlayerLastSeenPresentation = {
  label: string
  title: string
}

const minuteMs = 60_000
const hourMs = 60 * minuteMs
const dayMs = 24 * hourMs

export function playerPublicStatusPresentation(
  status: PublicPlayerStatus | null | undefined,
  now: Date = new Date(),
): PlayerPublicStatusPresentation | null {
  if (!status) return null

  const liveID = status.game_activity?.live_id?.trim() || null
  if (status.game_activity?.kind === 'playing') {
    return activityPresentation('playing', 'В игре', liveID)
  }
  if (status.game_activity?.kind === 'spectating') {
    return activityPresentation('spectating', 'Смотрит игру', liveID)
  }
  if (status.presence?.online) {
    return {
      kind: 'site-online',
      label: 'На сайте',
      liveID: null,
      title: 'Сейчас на сайте',
    }
  }

  const lastSeen = playerLastSeenPresentation(status.presence?.last_seen_at, now)
  if (!lastSeen) return null

  return {
    kind: 'offline',
    label: lastSeen.label,
    liveID: null,
    title: lastSeen.title,
  }
}

export function playerLastSeenPresentation(
  rawLastSeenAt: string | null | undefined,
  now: Date = new Date(),
): PlayerLastSeenPresentation | null {
  if (!rawLastSeenAt) return null
  const lastSeen = new Date(rawLastSeenAt)
  if (!Number.isFinite(lastSeen.getTime())) return null
  return {
    label: `Был в сети ${formatLastSeenDistance(lastSeen, now)}`,
    title: `Последний раз в сети: ${formatAbsoluteLastSeen(lastSeen)}`,
  }
}

export function playerPublicStatusRequestIsCurrent(
  expected: PlayerPublicStatusRequestFence,
  current: PlayerPublicStatusRequestFence,
): boolean {
  return expected.requestID === current.requestID && expected.steamID === current.steamID
}

export function formatLastSeenDistance(lastSeen: Date, now: Date = new Date()): string {
  const lastSeenValue = lastSeen.getTime()
  const nowValue = now.getTime()
  if (!Number.isFinite(lastSeenValue) || !Number.isFinite(nowValue)) return ''

  const elapsed = Math.max(0, nowValue - lastSeenValue)
  if (elapsed < minuteMs) return 'только что'
  if (elapsed < hourMs) {
    const minutes = Math.max(1, Math.floor(elapsed / minuteMs))
    return `${minutes} ${russianCountLabel(minutes, 'минуту', 'минуты', 'минут')} назад`
  }
  if (elapsed < dayMs) {
    const hours = Math.max(1, Math.floor(elapsed / hourMs))
    return `${hours} ${russianCountLabel(hours, 'час', 'часа', 'часов')} назад`
  }
  if (elapsed < 7 * dayMs) {
    const days = Math.max(1, Math.floor(elapsed / dayMs))
    return `${days} ${russianCountLabel(days, 'день', 'дня', 'дней')} назад`
  }

  const includeYear = lastSeen.getFullYear() !== now.getFullYear()
  const date = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(includeYear ? { year: 'numeric' as const } : {}),
  }).format(lastSeen)
  const time = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(lastSeen)
  return `${date} в ${time}`
}

function activityPresentation(
  kind: 'playing' | 'spectating',
  label: string,
  liveID: string | null,
): PlayerPublicStatusPresentation {
  return {
    kind,
    label,
    liveID,
    title: liveID ? `${label}: открыть матч` : label,
  }
}

export function formatAbsoluteLastSeen(lastSeen: Date): string {
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'long',
    timeStyle: 'medium',
  }).format(lastSeen)
}

function russianCountLabel(
  value: number,
  one: string,
  few: string,
  many: string,
): string {
  const modulo100 = Math.abs(value) % 100
  if (modulo100 >= 11 && modulo100 <= 14) return many
  const modulo10 = modulo100 % 10
  if (modulo10 === 1) return one
  if (modulo10 >= 2 && modulo10 <= 4) return few
  return many
}
